import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Container,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Grid,
  Chip,
  OutlinedInput,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Collapse,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import { useSchools } from '../contexts/SchoolsContext';
import { usePrograms } from '../contexts/ProgramsContext';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import { calculateDistance } from '../utils/distance';
import { getHighSchoolLocation, getHighSchoolLocationByName, HighSchoolLocation } from '../utils/highSchoolLocation';
import { useUserFavorites } from '../hooks/useUserFavorites';
import { activityLogService } from '../services/activityLogService';

// Define the options for each field
// Provinces will be dynamically populated from SchoolsContext

const DISTANCE_OPTIONS = [
  'up to 100 kms', 'up to 300 kms', 'up to 500 kms', 'up to 750 kms', '750+ kms'
];



// Sports will be dynamically populated from SchoolsContext and split into Men's and Women's options

const EXPERIENTIAL_LEARNING_OPTIONS = [
  'Show All', 'Show only those offering co-op'
];

const AdvancedSearch: React.FC = () => {
  const { schools } = useSchools();
  const { programs, getProgramEligibility, eligibilityCalculationInProgress } = usePrograms();
  const { user } = useUser();
  const { favorites } = useUserFavorites();
  const navigate = useNavigate();

  // Helper function to get the correct spelling based on user's country

  // Helper function to get eligibility color for program chips
  const getEligibilityColor = (programId: string) => {
    const eligibility = getProgramEligibility(programId);
    
    // If calculations are in progress and no eligibility data available, show blinking grey
    if (eligibilityCalculationInProgress && !eligibility) {
      return {
        backgroundColor: 'transparent',
        borderColor: '#ccc',
        color: '#666',
        isCalculating: true
      };
    }

    if (!eligibility) {
      // No eligibility data available - show light green (no prerequisites) with white text
      return {
        backgroundColor: '#8dd439',
        borderColor: '#8dd439',
        color: '#ffffff',
        isCalculating: false
      };
    }

    // Get user's grade from courses
    const userGrade = user?.courses ? 
      (Object.values(user.courses).some((course: any) => course.grade === '12' || course.grades === 12) ? '12' :
       Object.values(user.courses).some((course: any) => course.grade === '11' || course.grades === 11) ? '11' : '10') : '10';
    
    const isGrade10 = userGrade === '10';

    // Use the same logic as Program Profile to determine eligibility status
    const getEligibilityStatus = () => {
      // If isEligible is explicitly false, return false
      if (eligibility.ifEligible === false) {
        return false;
      }
      
      // If isEligible is explicitly true, return true
      if (eligibility.ifEligible === true) {
        return true;
      }
      
      // If isEligible is null, check for specific reasons why user might not be eligible
      if (eligibility.ifEligible === null) {
        const hasMissingCourses = (eligibility.missingCourses?.length || 0) > 0;
        const hasGradeIssues = (eligibility.gradeIssues?.length || 0) > 0;
        
        const hasLowAverage = userGrade === '12' &&
                             eligibility.calculatedAverage !== undefined &&
                             eligibility.programAverage !== undefined &&
                             eligibility.calculatedAverage < eligibility.programAverage;

        // If there are any specific reasons for not being eligible (missing courses, grade issues, or low average for G12),
        // then it should be classified as "Not Yet Eligible" (false).
        if (hasMissingCourses || hasGradeIssues || hasLowAverage) {
          return false;
        }
      }
      
      // Default to null for other cases (like truly no prerequisites)
      return null;
    };

    const eligibilityStatus = getEligibilityStatus();

    if (eligibilityStatus === true) {
      // Eligible - dark green with black text
      return {
        backgroundColor: '#7fc400',
        borderColor: '#000000',
        color: '#000000',
        isCalculating: false
      };
    } else if (eligibilityStatus === false) {
      // Not yet eligible - check for Grade 10 specific cases
      if (isGrade10) {
        const hasGradeIssues = (eligibility.gradeIssues?.length || 0) > 0;
        const hasMissingCourses = (eligibility.missingCourses?.length || 0) > 0;
        
        // Check if this program has early prerequisites (like "Six of 4M, 4U")
        const hasEarlyPrereqs = eligibility.alertMessage?.includes("Next year's courses will be prerequisites");
        
        if (hasEarlyPrereqs || hasGradeIssues) {
          // Grade 10: Yellow for early prerequisites or grade issues
          return {
            backgroundColor: '#ffc107',
            borderColor: '#ffc107',
            color: '#000000',
            isCalculating: false
          };
        } else if (hasMissingCourses) {
          // Grade 10: Blue for missing courses
          return {
            backgroundColor: '#1976d2',
            borderColor: '#1976d2',
            color: '#ffffff',
            isCalculating: false
          };
        }
      }
      
      // Grade 11/12 or Grade 10 fallback - blue
      return {
        backgroundColor: '#1976d2',
        borderColor: '#1976d2',
        color: '#ffffff',
        isCalculating: false
      };
    } else if (eligibilityStatus === null) {
      // No prerequisites - light green with white text
      return {
        backgroundColor: '#8dd439',
        borderColor: '#8dd439',
        color: '#ffffff',
        isCalculating: false
      };
    }

    // Fallback - light green (no prerequisites) with white text
    return {
      backgroundColor: '#8dd439',
      borderColor: '#8dd439',
      color: '#ffffff',
      isCalculating: false
    };
  };
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [totalPrograms, setTotalPrograms] = useState(0);
  const [isSearchFormCollapsed, setIsSearchFormCollapsed] = useState(false);
  const schoolFieldRef = useRef<HTMLDivElement>(null);
  const categoriesFieldRef = useRef<HTMLDivElement>(null);
  const eligibilityGuideRef = useRef<HTMLDivElement>(null);
  
  // Get unique provinces from schools data
  const availableProvinces = React.useMemo(() => {
    const provinces = new Set<string>();
    schools.forEach(school => {
      if (school.province && school.province.trim()) {
        provinces.add(school.province.trim());
      }
    });
    return Array.from(provinces).sort();
  }, [schools]);

  // Get unique men's sports from schools data (Male or Both)
  const availableMensSports = React.useMemo(() => {
    const sports = new Set<string>();
    schools.forEach(school => {
      if (school.athletics && Array.isArray(school.athletics)) {
        school.athletics.forEach(athletic => {
          if (athletic.sport && athletic.sport.trim() && 
              (athletic.gender === 'Male' || athletic.gender === 'Both')) {
            sports.add(athletic.sport.trim());
          }
        });
      }
    });
    const result = Array.from(sports).sort();
    console.log('🔄 AdvancedSearch: Available men\'s sports:', result);
    return result;
  }, [schools]);

  // Get unique women's sports from schools data (Female or Both)
  const availableWomensSports = React.useMemo(() => {
    const sports = new Set<string>();
    schools.forEach(school => {
      if (school.athletics && Array.isArray(school.athletics)) {
        school.athletics.forEach(athletic => {
          if (athletic.sport && athletic.sport.trim() && 
              (athletic.gender === 'Female' || athletic.gender === 'Both')) {
            sports.add(athletic.sport.trim());
          }
        });
      }
    });
    const result = Array.from(sports).sort();
    console.log('🔄 AdvancedSearch: Available women\'s sports:', result);
    return result;
  }, [schools]);

  // Get unique languages from programs data
  const availableLanguages = React.useMemo(() => {
    const languages = new Set<string>();
    programs.forEach(program => {
      if (program.language && program.language.trim()) {
        languages.add(program.language.trim());
      }
    });
    return Array.from(languages).sort();
  }, [programs]);

  // Get unique program categories from programs data
  const availableCategories = React.useMemo(() => {
    const categories = new Set<string>();
    programs.forEach(program => {
      if (program.category && program.category.trim()) {
        categories.add(program.category.trim());
      }
    });
    return Array.from(categories).sort();
  }, [programs]);

  // Get unique school types from schools data
  const availableSchoolTypes = React.useMemo(() => {
    const types = new Set<string>();
    schools.forEach(school => {
      if (school.type && school.type.trim()) {
        types.add(school.type.trim());
      }
    });
    return Array.from(types).sort();
  }, [schools]);

  // State to track if we should auto-run search after filter restoration
  const [shouldAutoSearch, setShouldAutoSearch] = useState(false);
  const [isReturningFromProgram, setIsReturningFromProgram] = useState(false);

  // Helper function to restore filter state
  const restoreFilterState = () => {
    const savedFilters = sessionStorage.getItem('advancedSearchFilters');
    if (savedFilters) {
      try {
        const filterState = JSON.parse(savedFilters);
        console.log('🔄 AdvancedSearch: Restoring filter state:', filterState);
        
        setSelectedSchools(filterState.selectedSchools || []);
        setSelectedCategories(filterState.selectedCategories || []);
        setProvinces(filterState.provinces || 'Show All');
        setDistances(filterState.distances || 'Show All');
        setMensSports(filterState.mensSports || 'Show All');
        setWomensSports(filterState.womensSports || 'Show All');
        setLanguages(filterState.languages || 'Show All');
        setExperientialLearning(filterState.experientialLearning || 'Show All');
        setSchoolType(filterState.schoolType || 'Show All');
        
        // Clear the saved filters after restoring
        sessionStorage.removeItem('advancedSearchFilters');
        
        // Set flag to auto-run search after state updates
        setShouldAutoSearch(true);
        console.log('🔄 AdvancedSearch: Set shouldAutoSearch to true');
        return true; // Indicates filters were restored
      } catch (error) {
        console.error('Error restoring filter state:', error);
        sessionStorage.removeItem('advancedSearchFilters');
      }
    } else {
      console.log('🔄 AdvancedSearch: No saved filters found');
    }
    return false; // Indicates no filters were restored
  };

  // Handle back navigation focus on fields
  useEffect(() => {
    const handleFocusSchoolField = () => {
      if (schoolFieldRef.current) {
        const input = schoolFieldRef.current.querySelector('input');
        if (input) {
          input.focus();
        }
      }
    };

    const handleFocusCategoriesField = () => {
      if (categoriesFieldRef.current) {
        const input = categoriesFieldRef.current.querySelector('input');
        if (input) {
          input.focus();
        }
      }
    };

    // Check if we should focus on school field from sidebar search
    const shouldFocusSchoolField = sessionStorage.getItem('focusSchoolField');
    if (shouldFocusSchoolField === 'true') {
      sessionStorage.removeItem('focusSchoolField');
      console.log('🔄 AdvancedSearch: Focusing on school field from sidebar search');
      setTimeout(handleFocusSchoolField, 100);
      return;
    }

    // Debug: Log all session storage keys
    console.log('🔄 AdvancedSearch: All session storage keys:', Object.keys(sessionStorage));
    
    // Check if we're returning from a school profile page
    const isReturningFromSchool = sessionStorage.getItem('returningFromSchoolProfile');
    console.log('🔄 AdvancedSearch: Checking for returningFromSchoolProfile:', isReturningFromSchool);
    if (isReturningFromSchool === 'true') {
      sessionStorage.removeItem('returningFromSchoolProfile');
      console.log('🔄 AdvancedSearch: Returning from school profile, attempting to restore filters...');
      
      // Try to restore filter state, fall back to focus if no filters
      if (!restoreFilterState()) {
        setTimeout(handleFocusSchoolField, 100);
      }
    }

    // Check if we're returning from advanced search (program profile)
    const isReturningFromAdvancedSearch = sessionStorage.getItem('returningFromAdvancedSearch');
    console.log('🔄 AdvancedSearch: Checking for returningFromAdvancedSearch:', isReturningFromAdvancedSearch);
    if (isReturningFromAdvancedSearch === 'true') {
      sessionStorage.removeItem('returningFromAdvancedSearch');
      console.log('🔄 AdvancedSearch: Returning from program profile, attempting to restore filters...');
      setIsReturningFromProgram(true);
      
      // Try to restore filter state, fall back to focus if no filters
      if (!restoreFilterState()) {
        setTimeout(handleFocusCategoriesField, 100);
      }
    }
  }, []);


  // Cleanup effect to clear saved filters when component unmounts
  useEffect(() => {
    return () => {
      // Only clear if we're not returning from a profile page
      const isReturningFromAdvancedSearch = sessionStorage.getItem('returningFromAdvancedSearch');
      const isReturningFromSchool = sessionStorage.getItem('returningFromSchoolProfile');
      if (!isReturningFromAdvancedSearch && !isReturningFromSchool) {
        sessionStorage.removeItem('advancedSearchFilters');
      }
    };
  }, []);

  // Handle clear all filters
  const handleClearFilters = () => {
    setSelectedSchools([]);
    setSelectedCategories([]);
    setProvinces('Show All');
    setDistances('Show All');
    setMensSports('Show All');
    setWomensSports('Show All');
    setLanguages('Show All');
    setExperientialLearning('Show All');
    setSchoolType('Show All');
    setSearchResults([]);
    setTotalPrograms(0);
    setIsSearchFormCollapsed(false);
    
    // Clear saved filter state from session storage
    sessionStorage.removeItem('advancedSearchFilters');
  };

  // Handle expanding the search form
  const handleExpandSearch = () => {
    setIsSearchFormCollapsed(false);
  };

  // Handle new search (clear results and expand form)
  const handleNewSearch = () => {
    setSearchResults([]);
    setTotalPrograms(0);
    setIsSearchFormCollapsed(false);
  };

  // Generate search summary chips for collapsed view
  const getSearchSummaryChips = () => {
    const chips = [];
    
    if (selectedSchools.length > 0) {
      chips.push(...selectedSchools.slice(0, 2).map(school => ({ label: school, type: 'school' })));
      if (selectedSchools.length > 2) {
        chips.push({ label: `+${selectedSchools.length - 2} more schools`, type: 'more' });
      }
    }
    
    if (selectedCategories.length > 0) {
      chips.push(...selectedCategories.slice(0, 2).map(category => ({ label: category, type: 'category' })));
      if (selectedCategories.length > 2) {
        chips.push({ label: `+${selectedCategories.length - 2} more categories`, type: 'more' });
      }
    }
    
    if (provinces !== 'Show All') {
      chips.push({ label: provinces, type: 'province' });
    }
    
    if (schoolType !== 'Show All') {
      chips.push({ label: schoolType, type: 'schoolType' });
    }
    
    if (distances !== 'Show All') {
      chips.push({ label: distances, type: 'distance' });
    }
    
    return chips;
  };

  // Handle school name click
  const handleSchoolClick = (schoolId: string) => {
    // Save current filter state before navigating
    const filterState = {
      selectedSchools,
      selectedCategories,
      provinces,
      distances,
      mensSports,
      womensSports,
      languages,
      experientialLearning,
      schoolType
    };
    console.log('🔄 AdvancedSearch: Saving filter state before school navigation:', filterState);
    sessionStorage.setItem('advancedSearchFilters', JSON.stringify(filterState));
    sessionStorage.setItem('returningFromSchoolProfile', 'true');
    console.log('🔄 AdvancedSearch: Set returningFromSchoolProfile flag to true');
    // Navigate to school profile
    navigate(`/schools/${schoolId}`);
  };

  // Handle program chip click
  const handleProgramClick = (programId: string) => {
    // Save current filter state before navigating
    const filterState = {
      selectedSchools,
      selectedCategories,
      provinces,
      distances,
      mensSports,
      womensSports,
      languages,
      experientialLearning,
      schoolType
    };
    console.log('🔄 AdvancedSearch: Saving filter state before program navigation:', filterState);
    sessionStorage.setItem('advancedSearchFilters', JSON.stringify(filterState));
    sessionStorage.setItem('returningFromAdvancedSearch', 'true');
    console.log('🔄 AdvancedSearch: Set returningFromAdvancedSearch flag to true');
    // Navigate to program profile
    navigate(`/programs/${programId}`);
  };

  // Helper function to check if a school is favorited
  const isSchoolFavorited = (schoolName: string) => {
    return favorites.some(fav => fav.type === 'school' && fav.name === schoolName);
  };

  // Helper function to check if a program is favorited
  const isProgramFavorited = (programId: string) => {
    return favorites.some(fav => fav.type === 'program' && fav.name === programId);
  };
  
  // State for form fields - single values with "Show All" defaults
  const [selectedSchools, setSelectedSchools] = useState<string[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [provinces, setProvinces] = useState<string>('Show All');
  const [distances, setDistances] = useState<string>('Show All');
  const [mensSports, setMensSports] = useState<string>('Show All');
  const [womensSports, setWomensSports] = useState<string>('Show All');
  const [languages, setLanguages] = useState<string>('Show All');
  const [experientialLearning, setExperientialLearning] = useState<string>('Show All');
  const [schoolType, setSchoolType] = useState<string>('Show All');

  // Handle single select change
  const handleSingleSelectChange = (
    event: SelectChangeEvent<string>,
    setter: React.Dispatch<React.SetStateAction<string>>
  ) => {
    setter(event.target.value);
  };

  // Handle multi-select changes
  const handleMultiSelectChange = (
    event: SelectChangeEvent<string[]>,
    setter: React.Dispatch<React.SetStateAction<string[]>>
  ) => {
    const value = event.target.value;
    setter(typeof value === 'string' ? value.split(',') : value);
  };

  // Check if all filters are set to default values
  const isAllFiltersDefault = () => {
    return selectedSchools.length === 0 &&
           selectedCategories.length === 0 &&
           provinces === 'Show All' &&
           distances === 'Show All' &&
           mensSports === 'Show All' &&
           womensSports === 'Show All' &&
           languages === 'Show All' &&
           experientialLearning === 'Show All' &&
           schoolType === 'Show All';
  };

  // Check if only school filter is applied with a single school
  const isOnlySingleSchoolFilter = () => {
    return selectedSchools.length === 1 &&
           selectedCategories.length === 0 &&
           provinces === 'Show All' &&
           distances === 'Show All' &&
           mensSports === 'Show All' &&
           womensSports === 'Show All' &&
           languages === 'Show All' &&
           experientialLearning === 'Show All' &&
           schoolType === 'Show All';
  };

  // Check if only program category filter is applied with a single category
  const isOnlySingleCategoryFilter = () => {
    return selectedSchools.length === 0 &&
           selectedCategories.length === 1 &&
           provinces === 'Show All' &&
           distances === 'Show All' &&
           mensSports === 'Show All' &&
           womensSports === 'Show All' &&
           languages === 'Show All' &&
           experientialLearning === 'Show All' &&
           schoolType === 'Show All';
  };

  // Helper function to format search criteria for logging
  const formatSearchCriteria = (criteria: {
    selectedSchools: string[];
    selectedCategories: string[];
    provinces: string;
    distances: string;
    mensSports: string;
    womensSports: string;
    languages: string;
    experientialLearning: string;
    schoolType: string;
  }) => {
    const parts: string[] = [];
    
    // Add schools if selected
    if (criteria.selectedSchools.length > 0) {
      parts.push(`Schools: ${criteria.selectedSchools.join(', ')}`);
    }
    
    // Add categories if selected
    if (criteria.selectedCategories.length > 0) {
      parts.push(`Categories: ${criteria.selectedCategories.join(', ')}`);
    }
    
    // Add other filters if not default
    if (criteria.provinces !== 'Show All') {
      parts.push(`Province: ${criteria.provinces}`);
    }
    
    if (criteria.distances !== 'Show All') {
      parts.push(`Distance: ${criteria.distances}`);
    }
    
    if (criteria.mensSports !== 'Show All') {
      parts.push(`Men's Sports: ${criteria.mensSports}`);
    }
    
    if (criteria.womensSports !== 'Show All') {
      parts.push(`Women's Sports: ${criteria.womensSports}`);
    }
    
    if (criteria.languages !== 'Show All') {
      parts.push(`Language: ${criteria.languages}`);
    }
    
    if (criteria.experientialLearning !== 'Show All') {
      parts.push(`Experiential Learning: ${criteria.experientialLearning}`);
    }
    
    if (criteria.schoolType !== 'Show All') {
      parts.push(`School Type: ${criteria.schoolType}`);
    }
    
    return parts.length > 0 ? parts.join('; ') : 'All filters default';
  };

  // Handle search button click
  const handleSearch = useCallback(async () => {
    console.log('Advanced Search Criteria:', {
      selectedSchools,
      selectedCategories,
      provinces,
      distances,
      mensSports,
      womensSports,
      languages,
      experientialLearning,
      schoolType
    });

    // Check if all filters are default
    if (isAllFiltersDefault()) {
      alert('Please select at least one filter criteria to search.');
      return;
    }

    // Check if only single school is selected
    if (isOnlySingleSchoolFilter()) {
      const selectedSchool = selectedSchools[0];
      const school = schools.find(s => s.name === selectedSchool);
      if (school) {
        // Set flag for back navigation focus
        sessionStorage.setItem('returningFromSchoolProfile', 'true');
        // Navigate to school profile page
        navigate(`/schools/${school.id}`);
        return;
      }
    }

    // Check if only single program category is selected
    if (isOnlySingleCategoryFilter()) {
      const selectedCategory = selectedCategories[0];
      // Set flag for back navigation
      sessionStorage.setItem('returningFromAdvancedSearch', 'true');
      // Navigate to programs page with category filter
      navigate(`/programs?category=${encodeURIComponent(selectedCategory)}`);
      return;
    }

    // Continue with normal search logic
    setIsSearching(true);
    try {
      // Step 1: Filter schools by selected schools
      let filteredSchools = schools;
      if (selectedSchools.length > 0) {
        filteredSchools = schools.filter(school => selectedSchools.includes(school.name));
      }

      // Step 2: Filter schools by men's sports
      if (mensSports !== 'Show All') {
        filteredSchools = filteredSchools.filter(school => {
          if (!school.athletics || school.athletics.length === 0) return false;
          const mensAthletics = school.athletics.filter((a: any) => 
            a.sport === mensSports && (a.gender === 'Male' || a.gender === 'Both')
          );
          return mensAthletics.length > 0;
        });
      }

      // Step 2b: Filter schools by women's sports
      if (womensSports !== 'Show All') {
        filteredSchools = filteredSchools.filter(school => {
          if (!school.athletics || school.athletics.length === 0) return false;
          const womensAthletics = school.athletics.filter((a: any) => 
            a.sport === womensSports && (a.gender === 'Female' || a.gender === 'Both')
          );
          return womensAthletics.length > 0;
        });
      }

      // Step 2c: Filter schools by school type
      if (schoolType !== 'Show All') {
        filteredSchools = filteredSchools.filter(school => school.type === schoolType);
      }

      // Step 2d: Filter schools by province
      if (provinces !== 'Show All') {
        filteredSchools = filteredSchools.filter(school => school.province === provinces);
      }

      // Step 3: Get user's high school location for distance calculations
      let userHighSchoolLocation: HighSchoolLocation | null = null;
      
      if (user?.schoolID) {
        userHighSchoolLocation = await getHighSchoolLocation(user.schoolID);
      }
      
      if (!userHighSchoolLocation && user?.schoolName) {
        userHighSchoolLocation = await getHighSchoolLocationByName(user.schoolName);
      }

      // Step 4: Filter by distance if not "Show All"
      if (distances !== 'Show All' && userHighSchoolLocation) {
        filteredSchools = filteredSchools.filter(school => {
          if (!school.SchoolLocationLatitude || !school.SchoolLocationLongitude) return false;
          
          const schoolLat = parseFloat(school.SchoolLocationLatitude);
          const schoolLon = parseFloat(school.SchoolLocationLongitude);
          
          if (isNaN(schoolLat) || isNaN(schoolLon)) return false;
          
          const distance = calculateDistance(
            userHighSchoolLocation!.latitude,
            userHighSchoolLocation!.longitude,
            schoolLat,
            schoolLon
          );

          // Apply distance filter based on selected criteria
          switch (distances) {
            case 'up to 100 kms':
              return distance <= 100;
            case 'up to 300 kms':
              return distance <= 300;
            case 'up to 500 kms':
              return distance <= 500;
            case 'up to 750 kms':
              return distance <= 750;
            case '750+ kms':
              return distance > 750;
            default:
              return true;
          }
        });
      }

      // Step 5: Get programs for filtered schools
      const schoolNames = filteredSchools.map(school => school.name);
      let filteredPrograms = programs.filter(program => schoolNames.includes(program.schoolName));

      // Step 6: Filter programs by categories
      if (selectedCategories.length > 0) {
        filteredPrograms = filteredPrograms.filter(program => 
          selectedCategories.includes(program.category)
        );
      }

      // Step 7: Filter programs by language
      if (languages !== 'Show All') {
        filteredPrograms = filteredPrograms.filter(program => program.language === languages);
      }

      // Step 8: Filter programs by experiential learning
      if (experientialLearning === 'Show only those offering co-op') {
        filteredPrograms = filteredPrograms.filter(program => {
          const expLearning = program.experientialLearning?.trim() || '';
          return expLearning !== '' && 
                 expLearning.toLowerCase() !== 'na' && 
                 expLearning.toLowerCase() !== 'n/a';
        });
      }

      // Step 9: Create results grouped by school with programs as chips
      const schoolProgramsMap = new Map<string, { school: any; programs: any[] }>();
      
      filteredPrograms.forEach(program => {
        const school = filteredSchools.find(s => s.name === program.schoolName);
        if (school) {
          if (!schoolProgramsMap.has(program.schoolName)) {
            schoolProgramsMap.set(program.schoolName, {
              school,
              programs: []
            });
          }
          schoolProgramsMap.get(program.schoolName)!.programs.push({
            name: program.program,
            id: program.id
          });
        }
      });

      const results = Array.from(schoolProgramsMap.values())
        .map(({ school, programs }) => {
          // Calculate distance if user location is available
          let distance: number | null = null;
          if (userHighSchoolLocation && school.SchoolLocationLatitude && school.SchoolLocationLongitude) {
            const schoolLat = parseFloat(school.SchoolLocationLatitude);
            const schoolLon = parseFloat(school.SchoolLocationLongitude);
            
            if (!isNaN(schoolLat) && !isNaN(schoolLon)) {
              distance = calculateDistance(
                userHighSchoolLocation.latitude,
                userHighSchoolLocation.longitude,
                schoolLat,
                schoolLon
              );
            }
          }

          return {
            schoolImage: school.feedIcon || school.image || school.resultsImage || '',
            schoolName: school.name,
            schoolId: school.id,
            city: school.city,
            province: school.province,
            type: school.type,
            distance,
            programs: programs.sort((a: any, b: any) => a.name.localeCompare(b.name)) // Sort programs alphabetically
          };
        })
        .sort((a, b) => {
          // Sort by distance (ascending), then by school name if distances are equal
          if (a.distance != null && b.distance != null) {
            if (a.distance !== b.distance) {
              return a.distance! - b.distance!;
            }
          } else if (a.distance != null && b.distance == null) {
            // If only one has distance, prioritize the one with distance
            return -1;
          } else if (a.distance == null && b.distance != null) {
            // If only one has distance, prioritize the one with distance
            return 1;
          }
          // If neither has distance or distances are equal, sort alphabetically
          return a.schoolName.localeCompare(b.schoolName);
        }); // Sort schools by distance (ascending), then alphabetically

      // Calculate total programs count
      const totalProgramsCount = results.reduce((sum, school) => sum + school.programs.length, 0);

      setSearchResults(results);
      setTotalPrograms(totalProgramsCount);
      console.log('Search results:', results);

      // Collapse the search form and scroll to results
      setIsSearchFormCollapsed(true);
      
      // Auto-scroll to Program Eligibility Guide after a short delay
      setTimeout(() => {
        eligibilityGuideRef.current?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
      }, 100);

      // Log the search activity
      if (user?.uid) {
        try {
          // Format search criteria for logging
          const searchCriteria = formatSearchCriteria({
            selectedSchools,
            selectedCategories,
            provinces,
            distances,
            mensSports,
            womensSports,
            languages,
            experientialLearning,
            schoolType
          });
          
          await activityLogService.logSearchRun(user.uid, searchCriteria);
        } catch (error) {
          console.error('Error logging search activity:', error);
          // Don't block the flow if logging fails
        }
      }

      // Note: Eligibility calculations are now handled by Firebase cache
      // The Advanced Search will use getProgramEligibility which gets data from globalEligibilityCache
      if (results.length > 0 && user?.courses && Object.keys(user.courses).length > 0) {
        console.log('✅ AdvancedSearch: Using Firebase cache for eligibility data');
        console.log('📊 AdvancedSearch: Search results will use cached eligibility data');
      }
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setIsSearching(false);
    }
  }, [
    selectedSchools,
    selectedCategories,
    provinces,
    distances,
    mensSports,
    womensSports,
    languages,
    experientialLearning,
    schoolType,
    schools,
    programs,
    user,
    navigate
  ]);

  // Effect to auto-run search when filters are restored
  useEffect(() => {
    if (shouldAutoSearch) {
      console.log('🔄 AdvancedSearch: shouldAutoSearch is true, triggering search...');
      // Small delay to ensure all state updates are complete
      const timer = setTimeout(() => {
        console.log('🔄 AdvancedSearch: Executing auto-search...');
        handleSearch();
        setShouldAutoSearch(false);
        
        // If returning from program profile, scroll to results after search completes
        if (isReturningFromProgram) {
          setTimeout(() => {
            eligibilityGuideRef.current?.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'start' 
            });
            setIsReturningFromProgram(false);
          }, 200);
        }
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [shouldAutoSearch, handleSearch, isReturningFromProgram]);

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 4, pl: 1 }}>
      <Paper sx={{ p: 3 }}>
        {/* Search Summary Bar (shown when collapsed) */}
        {isSearchFormCollapsed && searchResults.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between', 
              alignItems: { xs: 'flex-start', sm: 'center' }, 
              gap: 2,
              p: 2,
              backgroundColor: '#f8f9fa',
              borderRadius: 1,
              border: '1px solid #e0e0e0'
            }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600 }}>
                  Search Results: {searchResults.length} schools found with {totalPrograms.toLocaleString()} programs
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {getSearchSummaryChips().map((chip, index) => (
                    <Chip
                      key={index}
                      label={chip.label}
                      size="small"
                      variant="outlined"
                      sx={{
                        backgroundColor: chip.type === 'more' ? '#e3f2fd' : '#fff',
                        borderColor: chip.type === 'more' ? '#1976d2' : '#ccc',
                        color: chip.type === 'more' ? '#1976d2' : '#666',
                        fontSize: '0.75rem'
                      }}
                    />
                  ))}
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={handleExpandSearch}
                  sx={{
                    borderColor: '#1976d2',
                    color: '#1976d2',
                    '&:hover': {
                      borderColor: '#1565c0',
                      backgroundColor: 'rgba(25, 118, 210, 0.04)',
                    },
                  }}
                >
                  Edit Search
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<SearchIcon />}
                  onClick={handleNewSearch}
                  sx={{
                    backgroundColor: '#1976d2',
                    '&:hover': {
                      backgroundColor: '#1565c0',
                    },
                  }}
                >
                  New Search
                </Button>
              </Box>
            </Box>
          </Box>
        )}

        {/* Search Form Header */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: { xs: 'flex-start', sm: 'space-between' }, 
          alignItems: { xs: 'flex-start', sm: 'flex-end' }, 
          mb: 3,
          gap: { xs: 2, sm: 0 }
        }}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              Search
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Use the filters below to find schools and programs that match your criteria.
            </Typography>
          </Box>
          
          {/* Clear Filters Button */}
          <Button
            variant="outlined"
            size="small"
            onClick={handleClearFilters}
            sx={{
              borderColor: '#ccc',
              color: '#666',
              '&:hover': {
                borderColor: '#999',
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
              alignSelf: { xs: 'flex-start', sm: 'flex-end' }
            }}
          >
            Clear Filters
          </Button>
        </Box>

        {/* Collapsible Search Form */}
        <Collapse in={!isSearchFormCollapsed}>

        <Grid container spacing={3}>
          {/* School Field */}
          <Grid item xs={12}>
            <FormControl fullWidth ref={schoolFieldRef}>
              <InputLabel id="school-label">School</InputLabel>
              <Select
                labelId="school-label"
                multiple
                value={selectedSchools}
                onChange={(e) => handleMultiSelectChange(e, setSelectedSchools)}
                input={<OutlinedInput label="School" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        All Schools
                      </Typography>
                    ) : (
                      selected.map((value) => (
                        <Chip key={value} label={value} size="small" />
                      ))
                    )}
                  </Box>
                )}
              >
                {schools
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((school) => (
                    <MenuItem key={school.id} value={school.name}>
                      {school.name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Program Categories Field */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth ref={categoriesFieldRef}>
              <InputLabel id="categories-label">Program Categories</InputLabel>
              <Select
                labelId="categories-label"
                multiple
                value={selectedCategories}
                onChange={(e) => handleMultiSelectChange(e, setSelectedCategories)}
                input={<OutlinedInput label="Program Categories" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        All Categories
                      </Typography>
                    ) : (
                      selected.map((value) => (
                        <Chip key={value} label={value} size="small" />
                      ))
                    )}
                  </Box>
                )}
              >
                {availableCategories.map((category) => (
                  <MenuItem key={category} value={category}>
                    {category}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* School Type Field */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="school-type-label">School Type</InputLabel>
              <Select
                labelId="school-type-label"
                value={schoolType}
                onChange={(e) => handleSingleSelectChange(e, setSchoolType)}
                input={<OutlinedInput label="School Type" />}
              >
                <MenuItem value="Show All">Show All</MenuItem>
                {availableSchoolTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Province Field */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="province-label">Province</InputLabel>
              <Select
                labelId="province-label"
                value={provinces}
                onChange={(e) => handleSingleSelectChange(e, setProvinces)}
                input={<OutlinedInput label="Province" />}
              >
                <MenuItem value="Show All">Show All</MenuItem>
                {availableProvinces.map((province) => (
                  <MenuItem key={province} value={province}>
                    {province}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Distance Field */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="distance-label">Distance</InputLabel>
              <Select
                labelId="distance-label"
                value={distances}
                onChange={(e) => handleSingleSelectChange(e, setDistances)}
                input={<OutlinedInput label="Distance" />}
              >
                <MenuItem value="Show All">Show All</MenuItem>
                {DISTANCE_OPTIONS.map((distance) => (
                  <MenuItem key={distance} value={distance}>
                    {distance}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>



          {/* Language Field */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="language-label">Language</InputLabel>
              <Select
                labelId="language-label"
                value={languages}
                onChange={(e) => handleSingleSelectChange(e, setLanguages)}
                input={<OutlinedInput label="Language" />}
              >
                <MenuItem value="Show All">Show All</MenuItem>
                {availableLanguages.map((language) => (
                  <MenuItem key={language} value={language}>
                    {language}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Experiential Learning Field */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="experiential-learning-label">Experiential Learning</InputLabel>
              <Select
                labelId="experiential-learning-label"
                value={experientialLearning}
                onChange={(e) => handleSingleSelectChange(e, setExperientialLearning)}
                input={<OutlinedInput label="Experiential Learning" />}
              >
                {EXPERIENTIAL_LEARNING_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Men's Sports Field */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel id="mens-sports-label">Men's Sports</InputLabel>
              <Select
                labelId="mens-sports-label"
                value={mensSports}
                onChange={(e) => handleSingleSelectChange(e, setMensSports)}
                input={<OutlinedInput label="Men's Sports" />}
              >
                <MenuItem value="Show All">Show All</MenuItem>
                {availableMensSports.map((sport) => (
                  <MenuItem key={sport} value={sport}>
                    {sport}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Women's Sports Field */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel id="womens-sports-label">Women's Sports</InputLabel>
              <Select
                labelId="womens-sports-label"
                value={womensSports}
                onChange={(e) => handleSingleSelectChange(e, setWomensSports)}
                input={<OutlinedInput label="Women's Sports" />}
              >
                <MenuItem value="Show All">Show All</MenuItem>
                {availableWomensSports.map((sport) => (
                  <MenuItem key={sport} value={sport}>
                    {sport}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Search and Cancel Buttons */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
              <Button
                variant="contained"
                size="large"
                onClick={handleSearch}
                disabled={isSearching}
                sx={{
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  backgroundColor: '#1976d2',
                  '&:hover': {
                    backgroundColor: '#1565c0',
                  },
                  boxShadow: '0px 2px 4px rgba(0,0,0,0.2)',
                }}
              >
                {isSearching ? 'Searching...' : 'Search'}
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate('/')}
                sx={{
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  borderColor: '#1976d2',
                  color: '#1976d2',
                  '&:hover': {
                    borderColor: '#1565c0',
                    backgroundColor: 'rgba(25, 118, 210, 0.04)',
                  },
                }}
              >
                Cancel
              </Button>
            </Box>
          </Grid>
        </Grid>
        </Collapse>
      </Paper>

      {/* Program Eligibility Guide */}
      {searchResults.length > 0 && (
        <Paper ref={eligibilityGuideRef} sx={{ p: 2, mt: 2, backgroundColor: '#f5f5f5', border: '1px solid #e0e0e0' }}>
          <Typography variant="body2" color="text.secondary">
            <strong>Program Eligibility Guide:</strong> Favorite programs are shown in <strong>BOLD</strong>. 
            <span style={{ color: '#7fc400', fontWeight: 'bold' }}> Green chips</span> show programs you are eligible for, 
            <span style={{ color: '#8dd439', fontWeight: 'bold' }}> Light Green chips</span> show programs where no prerequisites are available, and 
            <span style={{ color: '#1976d2', fontWeight: 'bold' }}> Blue chips</span> show programs you are Not Yet Eligible for.
          </Typography>
        </Paper>
      )}

      {/* Search Results */}
      {searchResults.length > 0 && (
        <Paper sx={{ p: 3, mt: 3 }}>
          <Typography variant="h5" component="h2" gutterBottom>
            Search Results ({searchResults.length} schools found with {totalPrograms.toLocaleString()} programs)
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 80 }}></TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>School Name</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {searchResults.map((result, index) => (
                  <TableRow key={index} hover>
                    <TableCell sx={{ verticalAlign: 'top' }}>
                      {result.schoolImage ? (
                        <Avatar
                          src={result.schoolImage}
                          alt={result.schoolName}
                          sx={{ width: 50, height: 50 }}
                        />
                      ) : (
                        <Avatar sx={{ width: 50, height: 50, bgcolor: 'grey.300' }}>
                          {result.schoolName.charAt(0)}
                        </Avatar>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        <Typography 
                          variant="body1" 
                          sx={{ 
                            fontWeight: 500,
                            cursor: 'pointer',
                            color: 'primary.main',
                            '&:hover': {
                              textDecoration: 'underline',
                            },
                          }}
                          onClick={() => handleSchoolClick(result.schoolId)}
                        >
                          {result.schoolName}
                        </Typography>
                        {isSchoolFavorited(result.schoolName) ? (
                          <StarIcon sx={{ ml: 1, color: '#7fc400', fontSize: 20 }} />
                        ) : (
                          <StarBorderIcon sx={{ ml: 1, color: 'grey.400', fontSize: 20 }} />
                        )}
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {result.city && result.province ? `${result.city}, ${result.province}` : result.province}
                        {result.type && ` • ${result.type}`}
                        {result.distance !== null && ` • ${Math.round(result.distance).toLocaleString()} km`}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {result.programs.map((program: any, programIndex: number) => {
                          const isFavorited = isProgramFavorited(program.id);
                          const eligibilityColors = getEligibilityColor(program.id);
                          
                          // Use eligibility colors for background, but add bold styling and thick border for favorites
                          const chipColors = eligibilityColors;
                          
                          return (
                            <Chip
                              key={programIndex}
                              label={program.name}
                              size="small"
                              variant="outlined"
                              onClick={() => handleProgramClick(program.id)}
                              sx={{
                                fontSize: '0.75rem',
                                height: 24,
                                cursor: 'pointer',
                                backgroundColor: chipColors.backgroundColor,
                                borderColor: isFavorited ? '#000000' : chipColors.borderColor,
                                borderWidth: isFavorited ? '2px' : '1px',
                                color: chipColors.color,
                                fontWeight: isFavorited ? 'bold' : 'normal',
                                // Add blinking animation for calculating chips
                                ...(chipColors.isCalculating && {
                                  animation: 'blink 1.5s ease-in-out infinite',
                                  '@keyframes blink': {
                                    '0%, 50%': {
                                      opacity: 1,
                                      borderColor: '#ccc',
                                    },
                                    '25%, 75%': {
                                      opacity: 0.6,
                                      borderColor: '#999',
                                    },
                                  },
                                }),
                                '&:hover': {
                                  backgroundColor: isFavorited 
                                    ? '#8dd439' 
                                    : eligibilityColors.backgroundColor === '#7fc400' 
                                      ? '#8dd439' 
                                      : eligibilityColors.backgroundColor === '#1976d2'
                                        ? '#1565c0'
                                        : eligibilityColors.backgroundColor === '#ffc107'
                                          ? '#ffb300'
                                          : eligibilityColors.backgroundColor === '#8dd439'
                                            ? '#9ee44a'
                                            : 'primary.light',
                                  color: isFavorited 
                                    ? '#000000' 
                                    : eligibilityColors.color,
                                },
                                '& .MuiChip-label': {
                                  px: 1,
                                },
                              }}
                            />
                          );
                        })}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* No Results Message */}
      {!isSearching && searchResults.length === 0 && (
        <Paper sx={{ p: 3, mt: 3 }}>
          <Typography variant="h6" color="text.secondary" align="center">
            No programs found matching your search criteria.
          </Typography>
        </Paper>
      )}
    </Container>
  );
};

export default AdvancedSearch;
